import React, { useEffect, useRef, useState } from "react";
import CurrencyFormat from "react-currency-format";
import { useMutation } from "react-query";
import { loadStripe, StripeCardElement } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Message, Form, Loader, Modal, Segment, Button, Table } from "semantic-ui-react";
import identitySelectors from "../selectors/identity";
import AppSettings from "../config";
import { useParams } from "react-router-dom";
import { ChargesTableWithPending } from "../features/permits/components/ChargesTableWithPending";
import "./ConnectedCheckoutForm.scss";
import { FocusScope } from "react-aria";
import { format } from "date-fns";
import usePermit from "../features/permits/hooks/permits/usePermit";

export const CheckoutForm = (props: any) => {
  const {
    balance,
    amount,
    convenienceAmount,
    clientSecret,
    onSuccess,
    processing,
    setProcessing,
    setCardCountry,
    internationalCardFee,
  } = props;
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  const [cardDetails, setCardDetails] = useState<{ brand: string; last4: string } | undefined>();
  const [cardInfoFetched, setCardInfoFetched] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setModalOpen(false);
    setProcessing(true);
    const cardElement = elements.getElement(CardElement) as StripeCardElement;
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });
    setErrorMessage(result?.error?.message);
    if (result?.paymentIntent?.status === "succeeded") {
      onSuccess(result);
    } else {
      setProcessing(false);
    }
  };

  const formattedAmount = parseFloat(convenienceAmount).toFixed(2);
  const paidValue = balance ? balance / 100 : amount;

  const handleCardChange = async () => {
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      return;
    }
    const cardToken = await stripe.createToken(cardElement);

    if (!cardToken.error) {
      setCardInfoFetched(true);
      cardToken.token?.card?.country && setCardCountry(cardToken.token.card.country);
      setCardDetails(cardToken.token.card);
    } else {
      setCardInfoFetched(false);
    }
  };

  return (
    <div>
      <div>
        <CardElement
          className='card-element'
          options={{
            style: {
              base: {
                // Improve placeholder contrast to meet WCAG requirements
                "::placeholder": { color: "rgba(0, 0, 0, 0.6)" },
              },
            },
          }}
          onChange={handleCardChange}
        />

        {errorMessage && <Message negative> {errorMessage} </Message>}
      </div>
      <div style={{ textAlign: "center" }}>
        <Modal
          as={Form}
          trigger={
            <Button style={{ marginTop: 24 }} disabled={!cardInfoFetched} primary type='submit' fluid>
              Next
            </Button>
          }
          scrolling={true}
          onClose={() => setModalOpen(false)}
          onOpen={() => setModalOpen(true)}
          open={!processing && modalOpen}
          closeIcon={true}
          size='tiny'
          aria-labelledby='confirmation-modal-header'
        >
          <FocusScope contain restoreFocus autoFocus>
            <Modal.Header id='confirmation-modal-header' as='h2'>
              Confirm your Payment
            </Modal.Header>
            <Modal.Content>
              <Segment basic style={{ padding: 0 }}>
                <Table size='small' style={{ fontSize: 14 }}>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell className='bold'>Date</Table.Cell>
                      <Table.Cell className='align-table-cell-right'>{format(new Date(), "MM/dd/yyyy")}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell className='bold'>Payment Method</Table.Cell>
                      <Table.Cell className='align-table-cell-right'>{cardDetails?.brand}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell className='bold'>Card Number</Table.Cell>
                      <Table.Cell className='align-table-cell-right'>
                        {`**** **** **** ${cardDetails?.last4}`}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell className='bold'>Total Amount</Table.Cell>
                      <Table.Cell className='align-table-cell-right'>
                        <CurrencyFormat
                          value={paidValue || 0}
                          fixedDecimalScale={true}
                          decimalScale={2}
                          displayType={"text"}
                          prefix={"$"}
                          thousandSeparator={true}
                        />
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
                <p style={{ fontSize: 13 }} className='subText'>
                  {`A non-refundable convenience fee of $${formattedAmount} is added for credit card payments.`}
                  {internationalCardFee > 0 && (
                    <p>
                      This fee includes an additional ${internationalCardFee.toFixed(2)} for using an international
                      card.
                    </p>
                  )}
                </p>
              </Segment>
            </Modal.Content>
            <Modal.Actions>
              <Button secondary onClick={() => setModalOpen(false)}>
                Back
              </Button>
              <Button primary onClick={handleSubmit}>
                Pay{" "}
                <CurrencyFormat
                  value={paidValue || 0}
                  fixedDecimalScale={true}
                  decimalScale={2}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"$"}
                />
              </Button>
            </Modal.Actions>
          </FocusScope>
        </Modal>
      </div>
    </div>
  );
};

const ConnectedCheckoutForm = (props: any) => {
  const {
    feeList,
    fees,
    subtotal,
    activeFees,
    paid,
    checkout,
    onSuccess,
    balance,
    type = "payment",
    waitForPending,
    hideCheckoutForm = false,
  } = props;
  const { id } = useParams<{ id: string }>();
  const userEmail = identitySelectors.getUserEmail();
  const [startCheckout, startCheckoutState] = useMutation(checkout);

  const permitQuery = usePermit({ permitId: id });

  const [pending, setPending] = useState<number[]>([]); // List of pending transaction amounts
  const [amount, setAmount] = useState(0);
  const [mainAmount, setMainAmount] = useState(0);
  const [convenienceAmount, setConvenienceAmount] = useState(0);
  const [clientSecret, setClientSecret] = useState<string>();
  const [error, setError] = useState<string>();
  const [receiptEmail] = useState<string | null>(userEmail);
  const [cardCountry, setCardCountry] = useState<string>("US");
  const [internationalCardFee, setInternationalCardFee] = useState<number>(0);
  const [stripePromise, setStripePromise] = useState<any>();
  const stripe_test_key = AppSettings.STRIPE_TEST_PUBLIC_KEY;
  const stripe_live_key = AppSettings.STRIPE_LIVE_PUBLIC_KEY;
  const numTransactions = useRef<number>(0); // # of transactions for this permit, not including disputes (used to track whether permit query has newly created transactions)
  const [processing, setProcessing] = useState(false);
  const permitQueryRef = useRef(permitQuery);

  useEffect(() => {
    const start = async () => {
      const response = (await startCheckout({
        receiptEmail,
        cardCountry,
      } as any)) as any;
      if (response?.ok) {
        const json = await response.json();
        setAmount(json.amount / 100.0);
        setMainAmount(json.main_amount / 100.0);
        setConvenienceAmount(json.convenience_total / 100.0);
        setClientSecret(json.clientSecret);
        setInternationalCardFee(json.international_card_fee / 100.0);

        const api_key = json.stripe_mode === "live" ? stripe_live_key : stripe_test_key;
        json.stripe_account &&
          setStripePromise(
            loadStripe(api_key, {
              stripeAccount: json.stripe_account,
            }),
          );
      } else {
        setError("Something went wrong!  Please try again later...");
      }
    };
    start();
  }, [startCheckout, cardCountry]);

  // Update permitQuery reference to match newest data
  useEffect(() => {
    if (permitQuery.isSuccess && permitQuery.data !== null && !(permitQuery.data as any).error)
      permitQueryRef.current = permitQuery;
  }, [permitQuery]);

  // Adds new transaction to the pending list, increments numTransactions
  const addPendingTransaction = (amount: number) => {
    setPending([...pending, amount]);
    numTransactions.current++;
  };

  if (!stripePromise) {
    {
      error && <Message negative>{error}</Message>;
    }
  }

  return (
    <>
      {/* charges table */}
      <ChargesTableWithPending
        user={"citizen"}
        feeList={feeList || fees}
        mainAmount={mainAmount}
        paid={paid}
        pending={pending}
        setPending={setPending}
        numTransactions={numTransactions}
        activeFees={activeFees}
        balance={balance}
        type={type}
        setProcessing={setProcessing}
        onSuccess={onSuccess}
        refetch={() => permitQuery.refetch()}
        permitQueryRef={permitQueryRef}
        queryName={"permitsCheckout"}
        subtotal={subtotal}
      />
      {error && stripePromise && <Message error>{error}</Message>}
      {startCheckoutState.isLoading && <Loader active />}

      {/* card input and pay button */}
      {!error && stripePromise && !hideCheckoutForm && (
        <Elements stripe={stripePromise}>
          <CheckoutForm
            amount={amount}
            convenienceAmount={convenienceAmount}
            clientSecret={clientSecret}
            onSuccess={(paymentIntent: any) => {
              if (waitForPending) addPendingTransaction(paymentIntent.paymentIntent.amount);
              else {
                setProcessing(false);
                onSuccess();
              }
            }}
            processing={processing || startCheckoutState.isLoading}
            setCardCountry={setCardCountry}
            internationalCardFee={internationalCardFee}
            setProcessing={setProcessing}
          />
        </Elements>
      )}
    </>
  );
};

export default ConnectedCheckoutForm;
